import {
  addRowAfter,
  backspace,
  Keymap,
  addColumnAfter,
} from '@atlaskit/editor-common/keymaps';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import CrossCircleIcon from '@atlaskit/icon/glyph/cross-circle';
import EditIcon from '@atlaskit/icon/glyph/edit';
import LinkIcon from '@atlaskit/icon/glyph/link';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import { TriggerProps } from '@atlaskit/popup';
import { Box, Flex, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import {
  getDatabaseContainerType,
  SendDatabaseDeleteEntryEventSources,
  sendDeleteEntryEvent,
  useAnalytics,
} from '@databases/analytics';
import { tooltip } from '@databases/contextual-menu';
import ContextualMenu, {
  ButtonItem,
  MenuItem,
  useContextualMenu,
} from '@databases/contextual-menu';
import { MenuData } from '@databases/contextual-menu';
import { useDialog } from '@databases/dialog';
import { useFlaggingCallback } from '@databases/flags';
import { InsertColumnAfterIcon, InsertRowBelowIcon } from '@databases/icons';
import { messages } from '@databases/intl/messages';
import {
  ADD_BELOW_SHORTCUT,
  ADD_RIGHT_SHORTCUT,
  CLEAR_SHORTCUT,
  SUBMIT_SHORTCUT,
} from '@databases/keyboard-shortcuts/shortcuts';
import usePanel from '@databases/panel/usePanel';
import { KeyboardShortcut, useComponent, useKeyboardShortcut } from '@databases/react';
import { DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { setHoverDeleteButton } from '@databases/redux/cellSelections/actions';
import {
  selectIsDeleteColumnsDisabled,
  isEntrySelected,
  selectIsHoverDeleteEntriesButton,
  selectIsHoverDeleteFieldsButton,
  selectIsValueSelected,
  selectSelectedEntireFieldIds,
  selectSelectedEntryIds,
  selectSelectedFieldIds,
  selectSelectedValuesCount,
} from '@databases/redux/cellSelections/selectors';
import {
  doClearValues,
  doDeleteEntries,
  doDeleteFields,
  doInsertEntries,
  doInsertFields,
  doSelectNewEntries,
  doSelectNewFields,
} from '@databases/redux/contextual-menu/actions';
import { DatabaseViewSettings } from '@databases/types/database/view';
import { copyHtmlToClipboard } from '@databases/utils/copy-paste';
import { getReferencedStatusContentRefs } from '@databases/utils/database/field-types/pageStatus';
import {
  getDatabaseEntryRequired,
  getDatabaseFieldRequired,
  getDatabaseValue,
} from '@databases/utils/database/utils';
import { selectBaseUrl } from '@databases/utils/redux/context/selectors';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { getDatabasePath } from '@databases/utils/urls';
import { isEqual } from 'lodash-es';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import { EditMode, FIELD_TYPES, FieldTypeCategory } from '../../field-types/fieldTypes';

import BulkEditValues from './BulkEditValues';
import { Position } from './type';

const elementBeforeIconWrapper = xcss({
  marginRight: 'space.negative.075',
});

const elementAfterShortCutWrapper = xcss({
  backgroundColor: 'color.background.neutral',
  color: 'color.text.subtlest',
  borderRadius: 'border.radius.050',
  padding: 'space.050',
  lineHeight: 1,
  fontSize: 11.67,
  alignSelf: 'flex-end',
});

enum SELECT_VALUE_MENU_ACTIONS {
  COPY_VALUE_LINK = 'copy_value_link',
  BULK_EDIT_VALUES = 'bulk_edit_values',
  ADD_FIELD_RIGHT = 'add_field_right',
  ADD_ENTRY_BELOW = 'add_entry_below',
  CLEAR_VALUES = 'clear_values',
  DELETE_ENTRIES = 'delete_entries',
  DELETE_FIELDS = 'delete_fields',
}

const SELECT_VALUE_SHORTCUTS: Record<
  SELECT_VALUE_MENU_ACTIONS,
  { keyboardShortCut: KeyboardShortcut[]; keyMap: Keymap } | undefined
> = {
  [SELECT_VALUE_MENU_ACTIONS.COPY_VALUE_LINK]: undefined,
  [SELECT_VALUE_MENU_ACTIONS.BULK_EDIT_VALUES]: undefined,
  [SELECT_VALUE_MENU_ACTIONS.ADD_FIELD_RIGHT]: {
    keyMap: addColumnAfter,
    keyboardShortCut: ADD_RIGHT_SHORTCUT,
  },
  [SELECT_VALUE_MENU_ACTIONS.ADD_ENTRY_BELOW]: {
    keyMap: addRowAfter,
    keyboardShortCut: ADD_BELOW_SHORTCUT,
  },
  [SELECT_VALUE_MENU_ACTIONS.CLEAR_VALUES]: {
    keyMap: backspace,
    keyboardShortCut: CLEAR_SHORTCUT,
  },
  [SELECT_VALUE_MENU_ACTIONS.DELETE_ENTRIES]: undefined,
  [SELECT_VALUE_MENU_ACTIONS.DELETE_FIELDS]: undefined,
};

type SelectValueMenuProps = {
  contentId: string;
  scope: string;
  entryId: string;
  fieldId: string;
  viewSettings?: DatabaseViewSettings;
  currentViewId: string;
  Trigger: (triggerProps: TriggerProps) => React.ReactNode;
  canEditContent: boolean;
  canEditFields: boolean;
  fieldTypeCategories?: FieldTypeCategory[];
};

const SelectValueMenu = React.memo(function SelectValueMenu({
  contentId,
  scope,
  entryId,
  fieldId,
  viewSettings,
  currentViewId,
  Trigger,
  canEditContent,
  canEditFields,
  fieldTypeCategories = [
    FieldTypeCategory.BASIC,
    FieldTypeCategory.PAGE,
    FieldTypeCategory.OTHER_DATABASES,
    FieldTypeCategory.JIRA,
  ],
}: SelectValueMenuProps) {
  const [
    canCopyValue,
    canInsertContent,
    canClearContent,
    canBulkEditValues,
    canDeleteEntries,
    canDeleteFields,
    selectedValuesCount,
    selectedFieldsCount,
    selectedEntriesCount,
    baseUrl,
    databasePath,
    isHoverDeleteEntriesButton,
    isHoverDeleteFieldsButton,
  ] = defaultStoreUtils.useEqualSelector((state: DefaultRootState) => {
    const database = selectDatabaseByContentIdRequired(state, {
      contentId,
    });
    const entry = getDatabaseEntryRequired(database, entryId);
    const field = getDatabaseFieldRequired(database, fieldId);
    const value = getDatabaseValue(database, field, entry);

    const isSelected = selectIsValueSelected(state, {
      entryId,
      fieldId,
    });

    const selectedEntryIds = isSelected
      ? selectSelectedEntryIds(state, {
          contentId,
          scope,
          viewSettings,
        })
      : [];

    const selectedFieldIds = isSelected
      ? selectSelectedFieldIds(state, {
          contentId,
          scope,
          viewSettings,
        })
      : [];

    const selectedFieldsCount = selectedFieldIds.length;
    const selectedEntriesCount = selectedEntryIds.length;
    const selectedValuesCount = isSelected
      ? selectSelectedValuesCount(state, {
          contentId,
          scope,
          viewSettings,
        })
      : 0;

    const isDeletionDisabled =
      isSelected &&
      selectIsDeleteColumnsDisabled(state, {
        contentId,
        scope,
        viewSettings,
      });

    const referencedContentRefsOfEntries = selectedEntryIds.flatMap((entryId) => {
      const entry = getDatabaseEntryRequired(database, entryId);
      return getReferencedStatusContentRefs({ database, field, entry }, state);
    });

    const canUsePageCategory = referencedContentRefsOfEntries.every((v) =>
      isEqual(v.contentId, referencedContentRefsOfEntries[0].contentId)
    );

    const category = FIELD_TYPES[field.type].category;
    const canUseCategory =
      fieldTypeCategories?.includes(FieldTypeCategory.PAGE) &&
      category === FieldTypeCategory.PAGE
        ? canUsePageCategory
        : fieldTypeCategories.includes(category);

    const canEditValue =
      FIELD_TYPES[field.type].editMode(
        {
          database,
          field,
          entry,
          value,
        },
        state
      ) !== EditMode.READ_ONLY;

    const isHoverDeleteEntriesButton =
      isSelected && selectIsHoverDeleteEntriesButton(state);
    const isHoverDeleteFieldsButton =
      isSelected && selectIsHoverDeleteFieldsButton(state);
    const canInsertContent = canEditContent && selectedValuesCount > 0;
    const canClearContent = canInsertContent && canEditValue;
    const canCopyValue = canEditContent && selectedValuesCount === 1;
    const canBulkEditValues =
      canEditContent &&
      selectedValuesCount > 1 &&
      selectedFieldsCount === 1 &&
      canEditValue &&
      canUseCategory;
    const canDeleteEntries = canEditContent && selectedValuesCount > 0;
    const canDeleteFields =
      canEditFields && selectedValuesCount > 0 && !isDeletionDisabled;
    return [
      canCopyValue,
      canInsertContent,
      canClearContent,
      canBulkEditValues,
      canDeleteEntries,
      canDeleteFields,
      selectedValuesCount,
      selectedFieldsCount,
      selectedEntriesCount,
      selectBaseUrl(state),
      getDatabasePath(database),
      isHoverDeleteEntriesButton,
      isHoverDeleteFieldsButton,
    ] as const;
  });

  const { formatMessage } = useIntl();
  const store = defaultStoreUtils.useStore();
  const dispatch = defaultStoreUtils.useDispatch();
  const client = useAnalytics();
  const menuData: MenuData = {
    type: 'value',
    entryId,
    fieldId,
  };
  const { isMenuOpen } = useContextualMenu(menuData);
  const { getData, hidePanel } = usePanel();
  const { fieldId: panelFieldId } = getData<'FieldSettingsPanel'>() || {};
  const { entryId: panelEntryId } = getData<'EntryViewPanel'>() || {};

  const [isCopied, setIsCopied] = useState(false);
  const handleCopyValueLink = useFlaggingCallback(() => {
    const url = new URL(baseUrl + databasePath);
    url.searchParams.set('contentId', contentId);
    url.searchParams.set('entryId', entryId);
    url.searchParams.set('fieldId', fieldId);

    copyHtmlToClipboard(url.toString());
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 3000);
  }, [baseUrl, databasePath, contentId, entryId, fieldId]);

  const handleInsertFields = useFlaggingCallback(
    async (position: Position) => {
      await dispatch(
        doSelectNewFields(contentId, () =>
          dispatch(
            doInsertFields(
              contentId,
              scope,
              viewSettings,
              currentViewId,
              position === Position.BEFORE
            )
          )
        )
      );
      await client.sendInsertRowEvent({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
      });
    },
    [client, contentId, currentViewId, dispatch, scope, viewSettings]
  );

  const handleInsertEntries = useFlaggingCallback(
    async (position: Position) => {
      await dispatch(
        doSelectNewEntries(contentId, () =>
          dispatch(
            doInsertEntries(contentId, scope, viewSettings, position === Position.BEFORE)
          )
        )
      );
      await client.sendInsertRowEvent({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
      });
    },
    [client, contentId, dispatch, scope, viewSettings]
  );

  const handleClearValues = useFlaggingCallback(() => {
    dispatch(doClearValues(contentId, scope, viewSettings));
  }, [contentId, dispatch, scope, viewSettings]);

  const handleDeleteEntries = useFlaggingCallback(() => {
    if (isEntrySelected(store.getState(), contentId, scope, viewSettings, panelEntryId)) {
      hidePanel();
    }
    dispatch(doDeleteEntries(contentId, scope, viewSettings));
    void sendDeleteEntryEvent(
      client,
      SendDatabaseDeleteEntryEventSources.ENTRY_CONTEXTUAL_MENU,
      store.getState(),
      contentId
    );
  }, [store, contentId, viewSettings, panelEntryId, dispatch, scope, client, hidePanel]);

  const { showDialog } = useDialog();
  const onHoverDeleteFieldButton = useCallback(() => {
    dispatch(setHoverDeleteButton({ value: 'fields' }));
  }, [dispatch]);

  const onLeaveDeleteButton = useCallback(() => {
    dispatch(setHoverDeleteButton({ value: null }));
  }, [dispatch]);

  const onHoverDeleteEntryButton = useCallback(() => {
    dispatch(setHoverDeleteButton({ value: 'entries' }));
  }, [dispatch]);

  const handleDeleteFields = useFlaggingCallback(() => {
    const fieldNames: string[] = [];
    const database = selectDatabaseByContentIdRequired(store.getState(), {
      contentId,
    });
    const selectedEntireFieldIds = selectSelectedEntireFieldIds(store.getState(), {
      contentId,
      scope,
      viewSettings,
    });
    selectedEntireFieldIds.forEach((fieldId) => {
      if (database.fieldsById[fieldId]) {
        fieldNames.push(database.fieldsById[fieldId].name);
      }
    });
    showDialog('ConfirmDialog', {
      heading: formatMessage(messages['field-settings.delete-field']),
      body: (
        <Stack space="space.150">
          <Box>
            <FormattedMessage
              {...messages[
                'field-settings.delete-multiple-fields-confirmation-message.fields'
              ]}
              values={{
                fieldsNumber: fieldNames.length,
                fields: <Text weight="bold">{fieldNames.join(', ')}</Text>,
              }}
            />
          </Box>
          <Box>
            {formatMessage(
              messages[
                'field-settings.delete-multiple-fields-confirmation-message.additional'
              ]
            )}
          </Box>
        </Stack>
      ),
      yesLabel: formatMessage(messages['delete-buttons.delete-field-button']),
      noLabel: formatMessage(messages['delete-buttons.delete-field-cancel-button']),
      appearance: 'danger',
      onClose: (result: boolean) => {
        onLeaveDeleteButton();
        if (result) {
          const selectedFieldIds = selectSelectedFieldIds(store.getState(), {
            contentId,
            scope,
            viewSettings,
          });
          if (selectedFieldIds.includes(panelFieldId)) {
            hidePanel();
          }
          dispatch(doDeleteFields(contentId, scope, viewSettings));
        }
      },
    });

    void client.sendDeleteFieldEvent({
      contentId,
      databaseContainer: getDatabaseContainerType(),
    });

    client.sendRemoveCalculationEvent({
      databaseContainer: getDatabaseContainerType(),
      fieldId,
      source: 'selectValueMenu',
      contentId: database.contentId,
    });
  }, [
    store,
    contentId,
    scope,
    viewSettings,
    showDialog,
    formatMessage,
    client,
    fieldId,
    onLeaveDeleteButton,
    panelFieldId,
    dispatch,
    hidePanel,
  ]);

  const copyValueLinkMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_VALUE_MENU_ACTIONS.COPY_VALUE_LINK,
      content: formatMessage(messages['value-actions.copy-link']),
      onClick: handleCopyValueLink,
      closeOnClick: false,
      tooltipDescription: isCopied
        ? formatMessage(messages['copy-link.tooltip'])
        : undefined,
      tooltipPosition: 'right',
      elemBefore: (
        <Inline
          spread="space-between"
          alignBlock="center"
          xcss={elementBeforeIconWrapper}
        >
          <LinkIcon label="" size="medium" />
        </Inline>
      ),
    }),
    [formatMessage, handleCopyValueLink, isCopied]
  );
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const closeSubmenu = useCallback(() => {
    setIsSubmenuOpen(false);
  }, []);
  const toggleSubmenu = useCallback(() => {
    setIsSubmenuOpen((isOpen) => !isOpen);
  }, []);

  const bulkEditMenuItemTrigger = useComponent(
    ({ ref, ...triggerProps }) => (
      <ButtonItem
        isSelected={isSubmenuOpen}
        iconAfter={<ChevronRightIcon label="" size="medium" />}
        iconBefore={<EditIcon label="" size="medium" />}
        onClick={toggleSubmenu}
        onMouseEnter={() => setIsSubmenuOpen(true)}
        ref={ref}
        {...triggerProps}
      >
        {formatMessage(messages['value-actions.bulk-edit-values'], {
          valuesCount: selectedValuesCount,
        })}
      </ButtonItem>
    ),
    [formatMessage, isSubmenuOpen, selectedValuesCount, toggleSubmenu]
  );

  const bulkEditValuesMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_VALUE_MENU_ACTIONS.BULK_EDIT_VALUES,
      name: formatMessage(messages['value-actions.bulk-edit-values'], {
        valuesCount: selectedValuesCount,
      }),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <EditIcon label="" size="medium" />
        </Flex>
      ),
      content: (
        <BulkEditValues
          scope={scope}
          contentId={contentId}
          viewSettings={viewSettings}
          removeSelectedContent={handleClearValues}
          Trigger={bulkEditMenuItemTrigger}
          isOpen={isSubmenuOpen}
          placement="right"
          offset={[0, 2]}
        />
      ),
      value: { name: SELECT_VALUE_MENU_ACTIONS.BULK_EDIT_VALUES },
    }),
    [
      bulkEditMenuItemTrigger,
      contentId,
      formatMessage,
      handleClearValues,
      isSubmenuOpen,
      scope,
      selectedValuesCount,
      viewSettings,
    ]
  );

  const addFieldRightMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_VALUE_MENU_ACTIONS.ADD_FIELD_RIGHT,
      content: formatMessage(messages['field-actions.add-right']),
      onClick: async () => {
        await handleInsertFields(Position.AFTER);
      },
      onMouseEnter: closeSubmenu,
      closeOnClick: true,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(
            SELECT_VALUE_SHORTCUTS[SELECT_VALUE_MENU_ACTIONS.ADD_FIELD_RIGHT]?.keyMap
          )}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <InsertColumnAfterIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [closeSubmenu, formatMessage, handleInsertFields]
  );

  const addEntryBelowMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_VALUE_MENU_ACTIONS.ADD_ENTRY_BELOW,
      content: formatMessage(messages['entry-actions.add-below']),
      onClick: async () => await handleInsertEntries(Position.AFTER),
      onMouseEnter: closeSubmenu,
      closeOnClick: true,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(
            SELECT_VALUE_SHORTCUTS[SELECT_VALUE_MENU_ACTIONS.ADD_ENTRY_BELOW]?.keyMap
          )}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <InsertRowBelowIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [closeSubmenu, formatMessage, handleInsertEntries]
  );

  const clearValuesMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_VALUE_MENU_ACTIONS.CLEAR_VALUES,
      content: formatMessage(messages['value-actions.clear-cells'], {
        valuesCount: selectedValuesCount,
      }),
      onClick: handleClearValues,
      onMouseEnter: onLeaveDeleteButton,
      closeOnClick: true,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(
            SELECT_VALUE_SHORTCUTS[SELECT_VALUE_MENU_ACTIONS.CLEAR_VALUES]?.keyMap
          )}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <CrossCircleIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [formatMessage, handleClearValues, onLeaveDeleteButton, selectedValuesCount]
  );

  const deleteEntriesMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_VALUE_MENU_ACTIONS.DELETE_ENTRIES,
      content: formatMessage(messages['entry-actions.delete'], {
        entryCount: selectedEntriesCount,
      }),
      onClick: () => {
        handleDeleteEntries();
        onLeaveDeleteButton();
      },
      closeOnClick: true,
      onMouseEnter: onHoverDeleteEntryButton,
      onMouseLeave: onLeaveDeleteButton,
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <TrashIcon
            label=""
            size="medium"
            primaryColor={
              isHoverDeleteEntriesButton
                ? token('color.icon.danger', colors.R500)
                : undefined
            }
          />
        </Flex>
      ),
    }),
    [
      formatMessage,
      handleDeleteEntries,
      isHoverDeleteEntriesButton,
      onHoverDeleteEntryButton,
      onLeaveDeleteButton,
      selectedEntriesCount,
    ]
  );

  const deleteFieldsMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_VALUE_MENU_ACTIONS.DELETE_FIELDS,
      content: formatMessage(messages['field-actions.delete'], {
        fieldCount: selectedFieldsCount,
      }),
      onClick: handleDeleteFields,
      closeOnClick: true,
      onMouseEnter: onHoverDeleteFieldButton,
      onMouseLeave: onLeaveDeleteButton,
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <TrashIcon
            label=""
            size="medium"
            primaryColor={
              isHoverDeleteFieldsButton
                ? token('color.icon.danger', colors.R500)
                : undefined
            }
          />
        </Flex>
      ),
    }),
    [
      formatMessage,
      handleDeleteFields,
      isHoverDeleteFieldsButton,
      onHoverDeleteFieldButton,
      onLeaveDeleteButton,
      selectedFieldsCount,
    ]
  );

  const menuItems = useMemo(() => {
    const firstGroup: MenuItem[] = [];
    if (canCopyValue) {
      firstGroup.push(copyValueLinkMenuItem);
    }
    if (canBulkEditValues) {
      firstGroup.push(bulkEditValuesMenuItem);
    }

    const secondGroup: MenuItem[] = [];
    if (canInsertContent) {
      secondGroup.push(addFieldRightMenuItem);
      secondGroup.push(addEntryBelowMenuItem);
    }
    if (canClearContent) {
      secondGroup.push(clearValuesMenuItem);
    }
    if (canDeleteEntries) {
      secondGroup.push(deleteEntriesMenuItem);
    }
    if (canDeleteFields) {
      secondGroup.push(deleteFieldsMenuItem);
    }
    return [{ items: firstGroup }, { items: secondGroup }];
  }, [
    addEntryBelowMenuItem,
    addFieldRightMenuItem,
    bulkEditValuesMenuItem,
    canBulkEditValues,
    canClearContent,
    canCopyValue,
    canDeleteEntries,
    canDeleteFields,
    canInsertContent,
    clearValuesMenuItem,
    copyValueLinkMenuItem,
    deleteEntriesMenuItem,
    deleteFieldsMenuItem,
  ]);

  useKeyboardShortcut(
    isMenuOpen && !isSubmenuOpen && canInsertContent
      ? SELECT_VALUE_SHORTCUTS[SELECT_VALUE_MENU_ACTIONS.ADD_FIELD_RIGHT]
          ?.keyboardShortCut
      : undefined,
    () => handleInsertFields(Position.AFTER)
  );
  useKeyboardShortcut(
    isMenuOpen && !isSubmenuOpen && canInsertContent
      ? SELECT_VALUE_SHORTCUTS[SELECT_VALUE_MENU_ACTIONS.ADD_ENTRY_BELOW]
          ?.keyboardShortCut
      : undefined,
    () => handleInsertEntries(Position.AFTER)
  );
  useKeyboardShortcut(
    isMenuOpen && !isSubmenuOpen && canInsertContent
      ? SELECT_VALUE_SHORTCUTS[SELECT_VALUE_MENU_ACTIONS.CLEAR_VALUES]?.keyboardShortCut
      : undefined,
    handleClearValues
  );
  useKeyboardShortcut(isMenuOpen && isSubmenuOpen ? SUBMIT_SHORTCUT : undefined, () => {
    closeSubmenu();
  });

  return (
    <ContextualMenu
      menuData={menuData}
      items={menuItems}
      onClose={closeSubmenu}
      trigger={Trigger}
      shouldDisableFocusLock // both needed to enable the initial focus of the flyout menu on hover
      shouldRenderToParent
    />
  );
});

export default SelectValueMenu;
